import { Controller } from "@hotwired/stimulus"
import Litepicker from "litepicker"

// --- Exported pure functions for property-based testing ---

/**
 * Returns the number of months to display based on viewport width.
 * @param {number} width - viewport width in pixels
 * @returns {number} 2 if width >= 768, otherwise 1
 */
export function getNumberOfMonths(width) {
  return width >= 768 ? 2 : 1
}

/**
 * Transforms API booked_dates response (exclusive end) to Litepicker lockDays format (inclusive end).
 * Subtracts one day from each event's `end` date.
 * @param {Array<{start: string, end: string}>} events - array of event objects from the API
 * @returns {Array<[string, string]>} array of [start, adjustedEnd] pairs in YYYY-MM-DD format
 */
export function transformBlockedDates(events) {
  return events.map(event => {
    const endDate = new Date(event.end + "T00:00:00")
    endDate.setDate(endDate.getDate() - 1)
    return [event.start, formatDateYMD(endDate)]
  })
}

/**
 * Formats a numeric value to exactly two decimal places.
 * @param {number} value
 * @returns {string} value formatted with two decimal places
 */
export function formatPrice(value) {
  return parseFloat(value).toFixed(2)
}

/**
 * Formats a Date object to YYYY-MM-DD string.
 * @param {Date} date
 * @returns {string} date in YYYY-MM-DD format
 */
export function formatDateYMD(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

// --- Stimulus Controller ---

export default class extends Controller {
  static targets = [
    "arrivalDate",
    "departureDate",
    "arrivalDisplay",
    "departureDisplay",
    "price",
    "priceDisplay",
    "priceBreakdown",
    "breakdownContent",
    "message",
    "clearLink",
    "guests"
  ]

  static values = {
    minimumStay: Number,
    currencySymbol: { type: String, default: "Fr." }
  }

  async connect() {
    this.formPicker = null
    this._resizeHandler = null
    this._messageTimeout = null
    this._blockedDateRanges = []
    this._dailyPrices = {}

    try {
      this._blockedDateRanges = await this.fetchBlockedDates()
    } catch (error) {
      console.error("Failed to fetch blocked dates:", error)
    }

    // Prefetch daily prices for the next 12 months
    try {
      await this.fetchDailyPrices()
    } catch (error) {
      console.error("Failed to fetch daily prices:", error)
    }
  }

  disconnect() {
    if (this.formPicker) {
      this.formPicker.destroy()
      this.formPicker = null
    }
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler)
      this._resizeHandler = null
    }
    if (this._messageTimeout) {
      clearTimeout(this._messageTimeout)
      this._messageTimeout = null
    }
  }

  // --- Blocked date fetching (Task 2.2) ---

  async fetchBlockedDates() {
    try {
      const response = await fetch("/api/bookings/booked_dates")
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const events = await response.json()
      return transformBlockedDates(events)
    } catch (error) {
      console.error("Error fetching blocked dates:", error)
      return []
    }
  }

  // --- Daily prices fetching ---

  async fetchDailyPrices() {
    const today = new Date()
    const endDate = new Date(today)
    endDate.setFullYear(endDate.getFullYear() + 1)

    const params = new URLSearchParams({
      start_date: formatDateYMD(today),
      end_date: formatDateYMD(endDate)
    })

    try {
      const response = await fetch(`/api/bookings/daily_prices?${params}`)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this._dailyPrices = data.prices || {}
    } catch (error) {
      console.error("Error fetching daily prices:", error)
      this._dailyPrices = {}
    }
  }

  renderDayPrices() {
    if (!this.formPicker) return

    // Query all visible day cells in the picker
    const container = this.formPicker.ui
    if (!container) return

    const dayCells = container.querySelectorAll(".day-item[data-time]")
    dayCells.forEach(cell => {
      // Skip if price label already rendered
      if (cell.querySelector(".day-price")) return

      const timestamp = parseInt(cell.getAttribute("data-time"), 10)
      if (!timestamp) return

      const date = new Date(timestamp)
      const dateStr = formatDateYMD(date)
      const price = this._dailyPrices[dateStr]

      if (price !== undefined && !cell.classList.contains("is-locked")) {
        const priceLabel = document.createElement("span")
        priceLabel.className = "day-price"
        priceLabel.textContent = `${this.currencySymbolValue} ${Math.round(price)}`
        cell.appendChild(priceLabel)
      }
    })
  }

  // --- Minimum stay fetching ---

  async fetchMinimumStay(arrivalDate) {
    const params = new URLSearchParams({ arrival_date: arrivalDate })
    const response = await fetch(`/api/bookings/minimum_stay?${params}`)
    const data = await response.json()
    return data.minimum_stay
  }

  // --- Date selection callback ---

  async onDateSelected(start, end) {
    // Guard against re-entrant calls (setDateRange triggers selected event)
    if (this._syncing) return
    this._syncing = true

    try {
      const startDate = start.dateInstance
      const endDate = end.dateInstance
      const arrivalDateStr = formatDateYMD(startDate)

      // Fetch effective minimum stay for the selected arrival date
      const defaultMinStay = this.minimumStayValue
      try {
        const effectiveMinStay = await this.fetchMinimumStay(arrivalDateStr)
        if (effectiveMinStay !== null && effectiveMinStay !== undefined && Number.isInteger(effectiveMinStay) && effectiveMinStay >= 0) {
          this.minimumStayValue = effectiveMinStay
          if (this.formPicker) {
            this.formPicker.setOptions({ minDays: effectiveMinStay === 0 ? 0 : effectiveMinStay + 1 })
          }
        }
      } catch (error) {
        console.error("Failed to fetch minimum stay, using default:", error)
        // Fall back to existing minimumStayValue — no change needed
      }

      const nights = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24))

      if (nights < this.minimumStayValue) {
        this.showError(`Minimum stay is ${this.minimumStayValue} nights. Please select a longer range.`)
        if (this.formPicker) this.formPicker.clearSelection()
        return
      }

      // Populate hidden fields
      this.arrivalDateTarget.value = formatDateYMD(startDate)
      this.departureDateTarget.value = formatDateYMD(endDate)

      // Update display elements with short date format
      const dateOpts = { month: "short", day: "numeric", year: "numeric" }
      this.arrivalDisplayTarget.textContent = startDate.toLocaleDateString("en-US", dateOpts)
      this.departureDisplayTarget.textContent = endDate.toLocaleDateString("en-US", dateOpts)

      // Show clear link
      if (this.hasClearLinkTarget) {
        this.clearLinkTarget.classList.remove("hidden")
      }

      // Fetch price
      this.fetchPrice()
    } finally {
      this._syncing = false
    }
  }

  // --- Price fetching and guest count handler (Task 2.5) ---

  async fetchPrice() {
    const arrival = this.arrivalDateTarget.value
    const departure = this.departureDateTarget.value
    const guests = this.hasGuestsTarget ? this.guestsTarget.value : 2

    if (!arrival || !departure) return

    const params = new URLSearchParams({
      arrival_date: arrival,
      departure_date: departure,
      number_of_guests: guests
    })

    try {
      const response = await fetch(`/api/bookings/price?${params}`, {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) {
        const errorText = await response.text()
        console.error("Price API error:", response.status, errorText)
        throw new Error(`HTTP ${response.status}`)
      }
      const data = await response.json()

      if (data.price !== undefined && data.price !== null && data.price > 0) {
        const formatted = formatPrice(data.price)
        this.priceTarget.value = formatted

        if (this.hasPriceDisplayTarget) {
          this.priceDisplayTarget.textContent = formatted
        }

        if (this.hasPriceBreakdownTarget && data.breakdown) {
          this.renderBreakdown(data.breakdown)
          this.priceBreakdownTarget.classList.remove("hidden")
        }
      }
    } catch (error) {
      console.error("Error fetching price:", error)
      this.priceTarget.value = ""
      if (this.hasPriceDisplayTarget) {
        this.priceDisplayTarget.innerHTML = "&mdash;"
      }
      if (this.hasPriceBreakdownTarget) {
        this.priceBreakdownTarget.classList.add("hidden")
      }
    }
  }

  renderBreakdown(breakdown) {
    if (!this.hasBreakdownContentTarget) return

    const lines = []

    // Accommodation line items
    if (breakdown.nights > 0) {
      lines.push(`<div class="flex justify-between items-baseline mb-1"><span class="font-medium text-gray-700">Accommodation (${breakdown.nights} night${breakdown.nights !== 1 ? 's' : ''})</span><span class="font-semibold text-gray-900">CHF ${formatPrice(breakdown.accommodation_total)}</span></div>`)

      // Nightly rate details
      const details = breakdown.nightly_groups.map(group => {
        const dateOpts = { month: "short", day: "numeric" }
        const startStr = new Date(group.start_date + "T00:00:00").toLocaleDateString("en-US", dateOpts)
        const endStr = new Date(group.end_date + "T00:00:00").toLocaleDateString("en-US", dateOpts)

        let dateLabel = group.nights === 1
          ? startStr
          : `${startStr} – ${endStr} (${group.nights} nights)`

        let badge = ""
        if (group.is_override && group.override_label) {
          badge = `<span class="inline-flex ml-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-medium">${group.override_label}</span>`
        }

        const rateStr = group.nights === 1
          ? `CHF ${formatPrice(group.rate)}`
          : `${group.nights} × CHF ${formatPrice(group.rate)}`

        return `<div class="flex justify-between text-xs text-gray-600"><span>${dateLabel}${badge}</span><span>${rateStr}</span></div>`
      }).join("")

      lines.push(`<div class="space-y-0.5 mb-2 pl-1">${details}</div>`)
    }

    // Extra guest fee
    if (breakdown.extra_guest_count > 0) {
      const guestLabel = breakdown.extra_guest_count === 1 ? "guest" : "guests"
      lines.push(`<div class="flex justify-between items-baseline py-1 border-t border-gray-200"><span class="text-gray-700">Extra guest fee <span class="text-xs text-gray-500">(${breakdown.extra_guest_count} ${guestLabel} × CHF ${formatPrice(breakdown.extra_guest_price_per_night)} × ${breakdown.nights} nights)</span></span><span class="font-semibold text-gray-900">CHF ${formatPrice(breakdown.extra_guest_total)}</span></div>`)
    }

    // Cleaning fee
    lines.push(`<div class="flex justify-between items-baseline py-1 border-t border-gray-200"><span class="text-gray-700">Cleaning fee</span><span class="font-semibold text-gray-900">CHF ${formatPrice(breakdown.cleaning_fee)}</span></div>`)

    this.breakdownContentTarget.innerHTML = lines.join("")
  }

  guestsChanged() {
    const arrival = this.arrivalDateTarget.value
    const departure = this.departureDateTarget.value

    if (arrival && departure) {
      this.fetchPrice()
    }
  }

  // --- Clear dates action (Task 2.6) ---

  clearDates(event) {
    if (event) event.preventDefault()

    // Clear form popup picker
    if (this.formPicker) {
      this.formPicker.clearSelection()
    }

    // Clear hidden fields
    this.arrivalDateTarget.value = ""
    this.departureDateTarget.value = ""

    // Reset display elements
    this.arrivalDisplayTarget.textContent = "Add date"
    this.departureDisplayTarget.textContent = "Add date"

    // Clear price
    this.priceTarget.value = ""

    // Reset price display header
    if (this.hasPriceDisplayTarget) {
      this.priceDisplayTarget.innerHTML = "&mdash;"
    }

    // Hide price breakdown
    if (this.hasPriceBreakdownTarget) {
      this.priceBreakdownTarget.classList.add("hidden")
    }

    // Hide clear link
    if (this.hasClearLinkTarget) {
      this.clearLinkTarget.classList.add("hidden")
    }
  }

  // --- Form date picker popup (Airbnb-style) ---

  openFormPicker(event) {
    event.preventDefault()

    // If popup already exists, just show it
    if (this.formPicker) {
      this.formPicker.show()
      return
    }

    // Create a popup Litepicker anchored to the arrival display field
    const months = getNumberOfMonths(window.innerWidth)

    this.formPicker = new Litepicker({
      element: this.arrivalDisplayTarget,
      elementEnd: this.departureDisplayTarget,
      inlineMode: false,
      singleMode: false,
      numberOfMonths: months,
      numberOfColumns: months,
      minDate: new Date(),
      maxDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
      minDays: this.minimumStayValue === 0 ? 0 : this.minimumStayValue + 1,
      lockDays: this._blockedDateRanges,
      disallowLockDaysInRange: true,
      autoApply: true,
      showTooltip: true,
      lang: "en-US",
      lockDaysFormat: "YYYY-MM-DD"
    })

    // Bind selected event on the instance
    this.formPicker.on("selected", (start, end) => {
      this.onDateSelected(start, end)
    })

    // Render day prices whenever the calendar renders (month change, open, etc.)
    this.formPicker.on("render", () => {
      requestAnimationFrame(() => this.renderDayPrices())
    })

    this.formPicker.show()
  }

  // --- Helper methods ---

  showError(message) {
    if (!this.hasMessageTarget) return

    this.messageTarget.textContent = message
    this.messageTarget.classList.remove("hidden")

    if (this._messageTimeout) {
      clearTimeout(this._messageTimeout)
    }

    this._messageTimeout = setTimeout(() => {
      this.messageTarget.classList.add("hidden")
      this.messageTarget.textContent = ""
      this._messageTimeout = null
    }, 4000)
  }

}
